import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatCurrency, formatNumber, getMonthName, getMonthNameShort, getCurrentYear } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Receipt,
  Calculator,
  Euro,
  CreditCard,
  Truck,
  Leaf
} from 'lucide-react';

const COLORS = ['#00CCBC', '#06C167', '#F59E0B', '#8B5CF6', '#EC4899', '#FF6B6B'];

export function DashboardPage() {
  const { api } = useAuth();
  const [year, setYear] = useState(getCurrentYear());
  const [month, setMonth] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [caMensuel, setCaMensuel] = useState([]);
  const [repartition, setRepartition] = useState([]);
  const [tvaData, setTvaData] = useState([]);
  const [caParMode, setCaParMode] = useState([]);
  const [caParCategorie, setCaParCategorie] = useState([]);
  const [ca7Jours, setCa7Jours] = useState([]);
  const [plateformes7Jours, setPlateformes7Jours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { annee: year };
        if (month) params.mois = month;

        const [kpiRes, caRes, repRes, tvaRes, journalRes, prevRes, platRes] = await Promise.all([
          api.get('/dashboard/kpis', { params }),
          api.get('/dashboard/ca-mensuel', { params: { annee: year } }),
          api.get('/dashboard/repartition-plateformes', { params }),
          api.get('/tva', { params: { annee: year } }),
          api.get('/journal-revenus', { params }),
          api.get('/ventes-prev', { params }),
          api.get('/revenus-plateformes', { params })
        ]);

        setKpis(kpiRes.data);
        setCaMensuel(caRes.data.map(d => ({
          ...d,
          moisLabel: getMonthNameShort(d.mois)
        })));
        setRepartition(repRes.data.filter(d => d.total > 0));
        setTvaData(tvaRes.data);

        // Calculate CA par mode de paiement
        const modeMap = {};
        journalRes.data.forEach(j => {
          if (!modeMap[j.mode_paiement]) {
            modeMap[j.mode_paiement] = { ht: 0, ttc: 0 };
          }
          modeMap[j.mode_paiement].ttc += j.montant_ttc;
          modeMap[j.mode_paiement].ht += j.base_ht;
        });
        setCaParMode(Object.entries(modeMap).map(([mode, vals]) => ({
          mode,
          ttc: vals.ttc,
          ht: vals.ht
        })).sort((a, b) => b.ttc - a.ttc));

        // Calculate CA par catégorie
        const catMap = {};
        prevRes.data.forEach(p => {
          const cat = p.categorie_interne || 'Autre';
          if (!catMap[cat]) catMap[cat] = 0;
          catMap[cat] += p.ca_prevu;
        });
        setCaParCategorie(Object.entries(catMap).map(([cat, val]) => ({
          categorie: cat,
          montant: val
        })).sort((a, b) => b.montant - a.montant));

        // Calculate last 7 days CA
        const today = new Date();
        const last7 = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          const dateStr = d.toISOString().split('T')[0];
          const dayData = journalRes.data.filter(j => j.date === dateStr);
          const ttc = dayData.reduce((s, j) => s + j.montant_ttc, 0);
          const ht = dayData.reduce((s, j) => s + j.base_ht, 0);
          last7.push({
            date: `${d.getDate()}/${d.getMonth() + 1}`,
            ttc,
            ht
          });
        }
        setCa7Jours(last7);

        // Plateformes 7 jours
        const plat7 = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          const dateStr = d.toISOString().split('T')[0];
          const dayData = platRes.data.filter(p => p.date === dateStr);
          const brut = dayData.reduce((s, p) => s + p.revenu_brut, 0);
          const net = dayData.reduce((s, p) => s + p.revenu_net, 0);
          plat7.push({
            date: `${d.getDate()}/${d.getMonth() + 1}`,
            brut,
            net
          });
        }
        setPlateformes7Jours(plat7);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, year, month]);

  // Calculate totals for plateformes
  const totalBrut = repartition.reduce((s, r) => s + (r.plateforme !== 'Sur place' ? (r.brut || 0) : 0), 0);
  const totalNet = repartition.filter(r => r.plateforme !== 'Sur place').reduce((s, r) => s + (r.total || 0), 0);
  const totalFraisMarketing = repartition.reduce((s, r) => s + (r.frais_marketing || 0), 0);
  const totalEcoContribution = repartition.reduce((s, r) => s + (r.eco_contribution || 0), 0);
  
  // TVA totals
  const tvaTotals = tvaData.reduce((acc, t) => ({
    collectee: acc.collectee + t.tva_collectee,
    deductible: acc.deductible + t.tva_deductible,
    aPayer: acc.aPayer + t.tva_a_payer
  }), { collectee: 0, deductible: 0, aPayer: 0 });

  if (loading || !kpis) {
    return (
      <div className="space-y-6">
        <div className="h-96 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const KPICard = ({ title, value, icon: Icon, color = 'primary', subtitle }) => (
    <Card className="bg-card border-border/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${color}/10`}>
            <Icon className={`w-5 h-5 text-${color}`} style={{ color }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{title}</p>
            <p className="text-lg font-bold tabular-nums" style={{ color }}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-[120px]" data-testid="year-filter">
            <SelectValue placeholder="Année" />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={month?.toString() || 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
          <SelectTrigger className="w-[160px]" data-testid="month-filter">
            <SelectValue placeholder="Tous les mois" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Main Grid - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN - OVERVIEW VENTES */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-center py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg">
            OVERVIEW VENTES
          </h2>

          {/* Ventes KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <KPICard
              title="CA Prévu"
              value={formatCurrency(kpis.ca_prevu)}
              icon={Euro}
              color="#22c55e"
            />
            <KPICard
              title="CA Réel"
              value={formatCurrency(kpis.ca_journal || 0)}
              icon={Wallet}
              color="#3b82f6"
            />
            <KPICard
              title="CA Total"
              value={formatCurrency(kpis.ca_reel)}
              icon={Wallet}
              color="#f59e0b"
            />
            <KPICard
              title="Réel HT"
              value={formatCurrency(kpis.ca_reel - (kpis.tva_collectee || 0))}
              icon={Receipt}
              color="#8b5cf6"
            />
            <KPICard
              title="Écart CA"
              value={formatCurrency(kpis.ecart_ca)}
              icon={kpis.ecart_ca >= 0 ? TrendingUp : TrendingDown}
              color={kpis.ecart_ca >= 0 ? '#22c55e' : '#ef4444'}
            />
          </div>

          {/* % Matières Premières - carte dédiée */}
          <Card className={`border-2 ${kpis.pct_matieres > 35 ? 'border-red-400 bg-red-50/50 dark:bg-red-950/10' : kpis.pct_matieres > 30 ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-950/10' : 'border-green-400 bg-green-50/50 dark:bg-green-950/10'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${kpis.pct_matieres > 35 ? 'bg-red-100 dark:bg-red-900/30' : kpis.pct_matieres > 30 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                    <Leaf className={`w-6 h-6 ${kpis.pct_matieres > 35 ? 'text-red-600' : kpis.pct_matieres > 30 ? 'text-amber-600' : 'text-green-600'}`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">% Matières Premières / CA</p>
                    <p className={`text-2xl font-bold tabular-nums ${kpis.pct_matieres > 35 ? 'text-red-600' : kpis.pct_matieres > 30 ? 'text-amber-600' : 'text-green-600'}`}>
                      {kpis.pct_matieres || 0}%
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Matières Premières</p>
                  <p className="text-lg font-bold tabular-nums">{formatCurrency(kpis.matieres_premieres || 0)}</p>
                  <p className="text-xs text-muted-foreground">sur {formatCurrency(kpis.ca_reel)} de CA</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CA Par Catégorie + CA Par Mode */}
          <div className="grid grid-cols-2 gap-4">
            {/* CA Prévisionnel Par Catégories */}
            <Card>
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-sm">CA Prévisionnel Par Catégories</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={caParCategorie.slice(0, 5)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} className="text-xs" />
                      <YAxis type="category" dataKey="categorie" width={80} className="text-xs" />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="montant" fill="#22c55e" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* CA Réelles Par mode de paiement */}
            <Card>
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-sm">CA Réelles Par mode de paiement</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={caParMode} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}€`} className="text-xs" />
                      <YAxis type="category" dataKey="mode" width={70} className="text-xs" />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="ht" name="Base HT" fill="#f97316" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="ttc" name="Montant TTC" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CA Réel Par Mois */}
          <Card>
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-sm">CA Réel Par Mois</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="h-[160px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={caMensuel}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="moisLabel" className="text-xs" />
                    <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} className="text-xs" />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="ca_prevu" name="CA Prévu" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="ca_reel" name="CA Réel" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Evolution du CA Prévisionnel Par Mois */}
          <Card>
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-sm">Evolution du CA Prévisionnel Par Mois</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="h-[140px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={caMensuel}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="moisLabel" className="text-xs" />
                    <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} className="text-xs" />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Line type="monotone" dataKey="ca_prevu" name="CA Prévu" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT COLUMN - OVERVIEW PLATEFORMES */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-center py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg">
            OVERVIEW PLATEFORMES
          </h2>

          {/* Plateformes KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPICard
              title="Revenus Brut"
              value={formatCurrency(totalBrut)}
              icon={CreditCard}
              color="#8b5cf6"
            />
            <KPICard
              title="Revenus Net"
              value={formatCurrency(totalNet)}
              icon={Wallet}
              color="#22c55e"
            />
            <KPICard
              title="Frais Marketing"
              value={formatCurrency(totalFraisMarketing)}
              icon={Truck}
              color="#f97316"
            />
            <KPICard
              title="Frais Eco"
              value={formatCurrency(totalEcoContribution)}
              icon={Leaf}
              color="#06b6d4"
            />
          </div>

          {/* CA/Plateforme + % CA/Plateforme */}
          <div className="grid grid-cols-2 gap-4">
            {/* CA / Plateforme */}
            <Card>
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-sm">CA / Plateforme (Brut vs Net)</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <div className="h-[160px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={repartition.filter(r => r.plateforme !== 'Sur place')}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="plateforme" className="text-xs" />
                      <YAxis tickFormatter={(v) => `${v.toFixed(0)}€`} className="text-xs" />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="brut" name="Revenu Brut" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="total" name="Revenu Net" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* % CA / Plateforme */}
            <Card>
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-sm">% CA / Plateforme</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <div className="h-[160px]">
                  {repartition.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={repartition}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={65}
                          paddingAngle={2}
                          dataKey="total"
                          nameKey="plateforme"
                        >
                          {repartition.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                      Aucune donnée
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* TVA Cards */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-blue-950/50 border-blue-800">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-blue-300">TVA Collectée</p>
                <p className="text-xl font-bold text-blue-400 tabular-nums">{formatCurrency(tvaTotals.collectee)}</p>
              </CardContent>
            </Card>
            <Card className="bg-orange-950/50 border-orange-800">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-orange-300">TVA Déductible</p>
                <p className="text-xl font-bold text-orange-400 tabular-nums">{formatCurrency(tvaTotals.deductible)}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-950/50 border-red-800">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-red-300">TVA à Payer</p>
                <p className="text-xl font-bold text-red-400 tabular-nums">{formatCurrency(tvaTotals.aPayer)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Total TVA Chart */}
          <Card>
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-sm">Total TVA (déductible, collectée, à payer)</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="h-[140px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'TVA Collectée', value: tvaTotals.collectee, fill: '#3b82f6' },
                    { name: 'TVA Déductible', value: tvaTotals.deductible, fill: '#f97316' },
                    { name: 'TVA à Payer', value: tvaTotals.aPayer, fill: '#22c55e' }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" className="text-xs" />
                    <YAxis tickFormatter={(v) => `${v.toFixed(0)}€`} className="text-xs" />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {[
                        { name: 'TVA Collectée', value: tvaTotals.collectee, fill: '#3b82f6' },
                        { name: 'TVA Déductible', value: tvaTotals.deductible, fill: '#f97316' },
                        { name: 'TVA à Payer', value: tvaTotals.aPayer, fill: '#22c55e' }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bottom Row - 7 derniers jours */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CA Réel Sept (7) derniers Jours */}
        <Card>
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-sm">CA Réel Sept (7) derniers Jours</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ca7Jours}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" className="text-xs" />
                  <YAxis tickFormatter={(v) => `${v.toFixed(0)}€`} className="text-xs" />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="ttc" name="Montant TTC" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="ht" name="Base HT" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* CA Plateformes Sept (7) derniers Jours */}
        <Card>
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-sm">CA Plateformes Sept (7) derniers Jours</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={plateformes7Jours}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" className="text-xs" />
                  <YAxis tickFormatter={(v) => `${v.toFixed(0)}€`} className="text-xs" />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="brut" name="Revenu Brut" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="net" name="Revenu Net" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
